import { authMutations, exercisesQueries } from '@therame/api-client';
import { logger } from '@src/lib/logger';

/**
 * Client side of the media-session cookie (ADR-0038): exercise media is
 * streamed by `<video>`/`<img>`/`<audio>` straight from the API, authenticated
 * by a path-bound HttpOnly cookie instead of a bearer header those elements
 * cannot send. JS never sees the cookie, so this module tracks only whether
 * one should be in place.
 *
 * Without a session (not yet minted, mint failed, or refused for TAN / api-key
 * / extern sessions) callers keep the bearer-authenticated blob download.
 */

/**
 * Stop using the cookie a little before the server-side expiry, so a URL
 * handed out now is not already dead when the element fetches it.
 */
const EXPIRY_SAFETY_MS = 60 * 1000;

let expiresAt: number | undefined;
let refused = false;
let inFlightMint: Promise<void> | undefined;
/**
 * Set when a clear starts and never reset: every caller leaves the page right
 * after (logout, TAN login, step-up, account deletion). Until then a token
 * refresh or a media retry must not set the cookie again; the next page load
 * starts without the flag.
 */
let cleared = false;

const isForbidden = (error: unknown) =>
  (error as { response?: { status?: number } } | undefined)?.response?.status === 403;

export const hasMediaSession = (): boolean =>
  expiresAt !== undefined && Date.now() < expiresAt - EXPIRY_SAFETY_MS;

/**
 * Sets or slides the cookie, unless a clear has started. Never throws: a
 * failed mint only means media falls back to the blob download.
 */
export const mintMediaSession = (): Promise<void> => {
  if (cleared) return Promise.resolve();
  inFlightMint ??= (async () => {
    try {
      const minted = await authMutations.mintMediaSession();
      // A clear may have started while this mint was in flight.
      if (!cleared) {
        expiresAt = minted.expiresAt;
      }
    } catch (error) {
      expiresAt = undefined;
      if (isForbidden(error)) {
        refused = true;
        // A previous user's cookie may still sit in this browser.
        await sendClear();
        return;
      }
      logger.warn({ msg: 'Minting the media session failed; using blob downloads', error });
    }
  })().finally(() => {
    inFlightMint = undefined;
  });
  return inFlightMint;
};

/**
 * Called on every access-token refresh. A 401 on a media element never reaches
 * JS, so the cookie has to slide along with the session rather than be renewed
 * on failure.
 */
export const renewMediaSession = async (): Promise<void> => {
  if (refused) return;
  await mintMediaSession();
};

const sendClear = async (): Promise<void> => {
  try {
    await authMutations.clearMediaSession();
  } catch (error) {
    logger.warn({ msg: 'Clearing the media session failed', error });
  }
};

/**
 * Logout must drop the cookie itself: `Clear-Site-Data` there spares cookies.
 * A renewal still in flight is waited for first, otherwise its `Set-Cookie`
 * could land after the clear and outlive the logout.
 */
export const clearMediaSession = async (): Promise<void> => {
  cleared = true;
  expiresAt = undefined;
  await inFlightMint;
  await sendClear();
};

/**
 * `crossorigin` for a media element showing `url`. API media must be fetched in
 * CORS mode with credentials: the frontend's `COEP: credentialless` strips the
 * cookie from no-cors requests, and Safari ignores COEP, so a forgotten
 * attribute only fails outside iOS. Everything else stays without it — the
 * built-in video host answers `Access-Control-Allow-Origin: *`, which a
 * credentialed request rejects.
 */
export const mediaCrossOrigin = (url: string | undefined): 'use-credentials' | undefined =>
  url && exercisesQueries.isMediaUrl(url) ? 'use-credentials' : undefined;

type MediaTarget = HTMLImageElement | HTMLMediaElement;

const retriedUrls = new WeakMap<MediaTarget, string>();

const resolveFailedMedia = (
  target: EventTarget | null,
): { element: MediaTarget; url: string } | undefined => {
  if (target instanceof HTMLSourceElement) {
    const parent = target.parentElement;
    return parent instanceof HTMLMediaElement ? { element: parent, url: target.src } : undefined;
  }
  if (target instanceof HTMLImageElement) {
    return { element: target, url: target.src };
  }
  if (target instanceof HTMLMediaElement) {
    return { element: target, url: target.currentSrc || target.src };
  }
  return undefined;
};

/**
 * `load()` pauses and rewinds, and nothing would start the training video or
 * the TTS voice again: neither autoplays, and TTS is played once per exercise.
 * So resume what was still meant to play (a `pause()` during the mint already
 * set `paused`) from where it stopped, unless the element switched source.
 */
const reloadMedia = (element: HTMLMediaElement, url: string) => {
  const resume = !element.paused;
  const position = element.currentTime;
  element.load();
  if (position > 0) {
    element.addEventListener(
      'loadedmetadata',
      () => {
        if (element.currentSrc === url) element.currentTime = position;
      },
      { once: true },
    );
  }
  if (resume) {
    element.play().catch((error: unknown) => {
      // A pause or another source before playback starts aborts the request.
      if (error instanceof DOMException && error.name === 'AbortError') return;
      logger.warn({ msg: 'Resuming media after reloading it failed', error });
    });
  }
};

/**
 * Backstop for an API media element that failed to load (most likely an
 * expired cookie): mint a fresh cookie and reload, once per element and URL.
 * Resolves `true` when it retried, `false` when the caller should treat the
 * error as final. Sliding renewal should keep this from ever firing.
 */
export const retryMediaOnce = async (event: Event): Promise<boolean> => {
  const failed = resolveFailedMedia(event.target);
  if (!failed || !exercisesQueries.isMediaUrl(failed.url)) return false;
  if (retriedUrls.get(failed.element) === failed.url) return false;

  retriedUrls.set(failed.element, failed.url);
  // TTS audio is never part of the page, so only a removal counts.
  const wasConnected = failed.element.isConnected;
  await mintMediaSession();

  // No cookie means the same URL fails again; the caller decides what to show.
  if (!hasMediaSession()) return false;
  // The element may have moved on to another exercise while the mint ran, or
  // been unmounted with it; a removed element would still fetch on load().
  if (resolveFailedMedia(event.target)?.url !== failed.url) return false;
  if (wasConnected && !failed.element.isConnected) return false;

  if (failed.element instanceof HTMLImageElement) {
    failed.element.src = failed.url;
  } else {
    reloadMedia(failed.element, failed.url);
  }
  return true;
};
